import React, { useState } from 'react';
import { Shield, Key, AlertCircle, Loader } from 'lucide-react';
import { mfaService } from '../../services/mfaService';

/**
 * MFA Verification component shown during login when user has MFA enabled
 * 
 * Usage in Login.jsx:
 * 
 * const [mfaRequired, setMfaRequired] = useState(false);
 * const [mfaToken, setMfaToken] = useState(null);
 * 
 * // In login handler, check for MFA response:
 * const response = await authService.login(email, password);
 * if (response.mfaRequired) {
 *   setMfaToken(response.mfaToken);
 *   setMfaRequired(true);
 *   return;
 * }
 * 
 * // In render:
 * {mfaRequired ? (
 *   <MfaVerification 
 *     mfaToken={mfaToken} 
 *     onSuccess={handleMfaSuccess}
 *     onCancel={() => {
 *       setMfaRequired(false);
 *       setMfaToken(null);
 *     }}
 *   />
 * ) : (
 *   // Regular login form
 * )}
 */

const MfaVerification = ({ mfaToken, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [isRecoveryCode, setIsRecoveryCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a verification code');
      return;
    }

    // Basic validation
    if (!isRecoveryCode && code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setVerifying(true);
      setError('');
      
      const response = await mfaService.verifyLogin(mfaToken, code, isRecoveryCode);
      
      // Pass the full auth response to parent
      onSuccess(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    
    if (isRecoveryCode) {
      // Recovery codes are alphanumeric with dashes (e.g., ABCD-1234)
      setCode(value.toUpperCase());
    } else {
      // TOTP codes are 6 digits only
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
    
    if (error) setError('');
  };

  const toggleMode = () => {
    setIsRecoveryCode(!isRecoveryCode);
    setCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Two-Factor Authentication
          </h1>
          <p className="text-gray-600 mt-2">
            {isRecoveryCode 
              ? 'Enter one of your recovery codes'
              : 'Enter the 6-digit code from your authenticator app'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRecoveryCode ? 'Recovery Code' : 'Verification Code'}
            </label>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              className={`input text-center ${
                isRecoveryCode 
                  ? 'text-lg tracking-wide font-mono uppercase' 
                  : 'text-2xl tracking-widest font-mono'
              }`}
              placeholder={isRecoveryCode ? 'XXXX-XXXX' : '000000'}
              autoFocus
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={verifying || !code.trim()}
            className="btn btn-primary w-full py-3 flex items-center justify-center gap-2"
          >
            {verifying ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </button>
        </form>

        {/* Toggle Mode */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={toggleMode}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
          >
            <Key size={14} />
            {isRecoveryCode 
              ? 'Use authenticator app instead'
              : 'Use a recovery code instead'
            }
          </button>
        </div>

        {/* Cancel */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back to login
          </button>
        </div>

        {/* Help Text */}
        {!isRecoveryCode && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              Open your authenticator app (Google Authenticator, Authy, etc.) 
              and enter the code shown for SuddyFlow.
            </p>
          </div>
        )}

        {isRecoveryCode && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800 text-center">
              Recovery codes can only be used once. After using this code, 
              it will be permanently invalidated.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MfaVerification;